import { useAuth0 } from '@auth0/auth0-react';
import { initializeClients } from 'api/clients';
import { setGetIdTokenClaims } from 'api/clients/ApiClient';
import MainPlaceholder from 'components/common/MainPlaceholder/MainPlaceholder';
import SettingsPanel from 'components/settings-panel/SettingsPanel';
import is from 'is_js';
import { useAppContext } from 'Main';
import React, { useEffect } from 'react';
import { Col, Spinner } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import FalconRoutes from 'routes';
import { checkAuth, selectAuthChecked } from 'store/auth';
import configSelectors from 'store/config/config.selectors';
import { getContractsThunk } from 'store/contracts';
import { fetchAllCustomersThunk } from 'store/customer';
import { fetchAllLocationsThunk } from 'store/locations';
import { fetchAllPoolsThunk } from 'store/pool';
import { fetchFeaturesThunk } from 'store/features';

const App = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, getIdTokenClaims, isLoading } = useAuth0();
  const isConfigLoading = useSelector(configSelectors.loading);
  const isAuthChecked = useSelector(selectAuthChecked);

  const HTMLClassList = document.getElementsByTagName('html')[0].classList;
  const {
    config: { navbarPosition }
  } = useAppContext();

  useEffect(() => {
    setGetIdTokenClaims(getIdTokenClaims);

    const initClients = async () => {
      await initializeClients();
    };

    initClients();
  }, [getIdTokenClaims]);

  useEffect(() => {
    dispatch(checkAuth());
    if (isAuthenticated) {
      // Startup fetches are limited to what globally-mounted code needs before any page
      // renders: features (route guards), contracts (Dashboard), customers (/tenant composite —
      // Dashboard + most pages), and locations + pools (routes/index.js builds dynamic routes
      // from them). Everything else is fetched on demand by the pages that render it, via the
      // useEnsure* hooks (invoices, payment requests, watcher links, miner types, tenant
      // transaction feeds, /location/calculations). getOrdersThunk was dropped outright — no
      // component reads state.billing.orders.
      dispatch(fetchFeaturesThunk());
      dispatch(getContractsThunk());
      dispatch(fetchAllCustomersThunk());
      dispatch(fetchAllLocationsThunk());
      dispatch(fetchAllPoolsThunk());
    }

    if (is.windows()) HTMLClassList.add('windows');
    if (is.chrome()) HTMLClassList.add('chrome');
    if (is.firefox()) HTMLClassList.add('firefox');
    if (is.safari()) HTMLClassList.add('safari');
  }, [HTMLClassList, dispatch, isAuthenticated]);

  useEffect(() => {
    if (navbarPosition === 'double-top') {
      HTMLClassList.add('double-top-nav-layout');
    }
    return () => HTMLClassList.remove('double-top-nav-layout');
  }, [navbarPosition]);

  if (isConfigLoading || !isAuthChecked) {
    return <MainPlaceholder />;
  }

  // Only Auth0 gates the shell. The customers fetch used to be awaited here too, which blanked
  // the whole SPA until the slowest startup call (/tenant) resolved — and re-blanked it whenever
  // any customer thunk set the slice's loading flag. Pages that need customers render their own
  // loading state (e.g. Dashboard/Customers via selectCustomersLoading).
  if (isLoading) {
    return (
      <Col xs={12}>
        <Spinner className="position-absolute start-50" animation="grow" />
      </Col>
    );
  }

  return (
    <Router basename={process.env.PUBLIC_URL}>
      <FalconRoutes />
      <SettingsPanel />
      <ToastContainer position="top-center" autoClose={2500} />
    </Router>
  );
};

export default App;
